import styled, {css} from "styled-components";
import React, {useCallback, useEffect, useRef, useState} from "react";
import {XyiconFeature} from "../../../generated/api/base";
import type {IModel} from "../../../data/models/Model";
import {DomUtils} from "../../../utils/dom/DomUtils";
import {IconButtonV5} from "../interaction/IconButtonV5";
import CloseIcon from "../icons/xmark-large.svg?react";
import BackArrowIcon from "../icons/arrow-left.svg?react";
import type {IMenu} from "../../../Navigation";
import {
	Flex,
	FlexCenter,
	FlexCenterStyle,
	TOPBAR_HEIGHT,
	VerticalFlex,
	VerticalFlexStyle,
	baseDistance,
	fontSize,
	fontWeight,
	radius,
	zIndex,
} from "../styles/styles";
import {colorPalette} from "../styles/colorPalette";
import {useAppStore} from "../../../StateManager";
import type {NavBarState} from "../../../StateManager";
import BigSearchIcon from "../icons/search-alt-2.svg?react";
import {TableFooterV5} from "../abstract/table/TableFooterV5";
import {getPagedData} from "../abstract/table/PaginationUtils";
import {SearchFeatureBlockStyled, SearchFeatureBlockV5} from "./SearchFeatureBlockV5";
import {getScopeWideSearchFeatures, type ScopeWideSearchType} from "./OrganizationWideSearchUtils";

const getFeatureMenuLabel = (feature: XyiconFeature, navigationMenus: IMenu[]): string => {
	// TODO Documents are missing in Navigation
	if ([XyiconFeature.OrganizationDocument, XyiconFeature.PortfolioDocument, XyiconFeature.Document].includes(feature)) {
		return "Documents";
	} else if (feature === XyiconFeature.Markup) {
		return "Markups";
	}

	return navigationMenus.find((menu) => menu.feature === feature).label ?? "";
};

const ItemsNumberWhenCollapsed = 15;
export const ItemsNumberWhenExpanded = 60;

interface IScopeWideSearchProps {
	readonly scopeWideSearchType: ScopeWideSearchType;
	readonly isLoading: boolean;
	readonly results: IModel[];
	readonly queryString: string;
	readonly closeScopeWideSearch: () => void;
}

export const ScopeWideSearchV5 = (props: IScopeWideSearchProps) => {
	const {queryString, closeScopeWideSearch, results, isLoading, scopeWideSearchType} = props;
	const appState = useAppStore((state) => state.appState);
	const navBarState: NavBarState = useAppStore((state) => state.navBarState);
	const [currentPage, setCurrentPage] = useState<number>(0);
	const _timeoutId = useRef<number>(null);
	const _element = useRef<HTMLDivElement>(null);
	const _viewport = useRef<VisualViewport>(window.visualViewport);

	const [expandedSearchFeatureBlock, setExpandedSearchFeatureBlock] = useState<XyiconFeature | null>(null);
	const [visibleSearchBlocks, setVisibleSearchBlocks] = useState<XyiconFeature[]>([]);

	const leaveExpandedMode = () => {
		setCurrentPage(0);
		setExpandedSearchFeatureBlock(null);
	};

	const startExpandedMode = (item: XyiconFeature) => {
		setExpandedSearchFeatureBlock(item);
	};

	const scrollToFeature = (feature: XyiconFeature) => {
		const featureBlocks = document.querySelectorAll(".SearchFeatureBlock h3");
		const divToScroll = Array.from(featureBlocks).find((node: HTMLElement) => node.dataset.featureid === feature.toString());

		if (divToScroll) {
			DomUtils.scrollIntoViewIfNeeded(divToScroll as HTMLElement);
		}
	};

	const stringifiedSearchBlocks1 = visibleSearchBlocks.toString();

	const updateVisibleSearchBlocks = useCallback(() => {
		const featureSearchBlocks = _element.current?.childNodes as NodeList;
		const visibleBlocks: XyiconFeature[] = [];

		const isElementInViewport = (el: HTMLDivElement) => {
			const element = el.getBoundingClientRect();

			// We need to check only the vieport first 1/3 (or at least 300px)
			let viewportBottom = _viewport.current.height * (1 / 3);

			viewportBottom = viewportBottom < 300 ? 300 : viewportBottom;

			return element.bottom < _viewport.current.height && element.top > _viewport.current.offsetTop;
		};

		featureSearchBlocks?.forEach((block: HTMLDivElement) => {
			if (isElementInViewport(block)) {
				if (block.dataset.featureid) {
					visibleBlocks.push(parseInt(block.dataset.featureid));
				}
			}
		});

		const stringifiedSearchBlocks2 = visibleBlocks.toString();

		// Avoid unnecessary re-render (which can be very expensive here)
		if (stringifiedSearchBlocks1 !== stringifiedSearchBlocks2) {
			setVisibleSearchBlocks(visibleBlocks);
		}
	}, [stringifiedSearchBlocks1]);

	const scrollHandle = (e: React.UIEvent) => {
		updateVisibleSearchBlocks();
	};

	const filterFilteredListByFeature = (filteredList: IModel[], feature: XyiconFeature) => {
		return filteredList.filter((item: IModel) => {
			if (feature === XyiconFeature.Document) {
				return [XyiconFeature.Document, XyiconFeature.OrganizationDocument, XyiconFeature.PortfolioDocument].includes(item.ownFeature);
			}

			return item.ownFeature === feature;
		});
	};

	const {onDetailsContainerClose} = appState.app;

	useEffect(() => {
		clearTimeout(_timeoutId.current);
		_timeoutId.current = window.setTimeout(updateVisibleSearchBlocks, 1000);
	}, [queryString, onDetailsContainerClose, updateVisibleSearchBlocks]);

	const filteredList = results;
	const isFilteredListEmpty = filteredList.length === 0;
	const isHome = ["portfolios", "catalog", "reports", "home"].includes(appState.selectedMenu);

	const scopeWideSearchFeatures = getScopeWideSearchFeatures(scopeWideSearchType);

	const sectionFeatures: XyiconFeature[] = [
		...scopeWideSearchFeatures.filter(
			(f) => ![XyiconFeature.Document, XyiconFeature.OrganizationDocument, XyiconFeature.PortfolioDocument].includes(f),
		),
		XyiconFeature.Document,
	];

	const moduleGroupContent = () => {
		if (expandedSearchFeatureBlock) {
			const arrayWithItems = filterFilteredListByFeature(filteredList, expandedSearchFeatureBlock);
			const pagedData = getPagedData(arrayWithItems, currentPage, ItemsNumberWhenExpanded);

			return (
				<>
					<SearchFeatureBlockV5
						scopeWideSearchType={scopeWideSearchType}
						feature={expandedSearchFeatureBlock}
						queryString={queryString}
						featureItems={pagedData.pagedData}
						startExpandedMode={(feature: XyiconFeature) => startExpandedMode(feature)}
						filteredLength={arrayWithItems.length}
						itemsNumberWhenCollapsed={ItemsNumberWhenCollapsed}
						isExpanded={true}
						menuLabel={getFeatureMenuLabel(expandedSearchFeatureBlock, appState.app.navigation.menus)}
						closeScopeWideSearch={closeScopeWideSearch}
					/>
					<TableFooterV5
						itemsPerPage={ItemsNumberWhenExpanded}
						totalCount={arrayWithItems.length}
						startIndex={pagedData.startIndex}
						lastIndex={pagedData.lastIndex}
						currentPage={pagedData.clampedCurrentPage}
						onPageChange={setCurrentPage}
					/>
				</>
			);
		} else {
			return sectionFeatures.map((queriedFeature: XyiconFeature) => {
				const arrayWithItems = filterFilteredListByFeature(filteredList, queriedFeature);

				if (arrayWithItems.length > 0) {
					return (
						<SearchFeatureBlockV5
							key={queriedFeature}
							scopeWideSearchType={scopeWideSearchType}
							feature={queriedFeature}
							queryString={queryString}
							featureItems={arrayWithItems.slice(0, !expandedSearchFeatureBlock ? ItemsNumberWhenCollapsed : ItemsNumberWhenExpanded)}
							startExpandedMode={(feature: XyiconFeature) => startExpandedMode(feature)}
							filteredLength={arrayWithItems.length}
							itemsNumberWhenCollapsed={ItemsNumberWhenCollapsed}
							isExpanded={false}
							menuLabel={getFeatureMenuLabel(queriedFeature, appState.app.navigation.menus)}
							closeScopeWideSearch={closeScopeWideSearch}
						/>
					);
				}
			});
		}
	};

	return (
		<ScopeWideSearchStyled
			onScroll={scrollHandle}
			$shrinkSize={navBarState === "docked" ? (isHome ? 200 : 288) : 48}
		>
			<HeaderStyled>
				<FlexCenter $gap={baseDistance.md}>
					{expandedSearchFeatureBlock && (
						<IconButtonV5
							IconComponent={BackArrowIcon}
							className="back"
							onClick={leaveExpandedMode}
						/>
					)}
					<Flex $gap="8px">
						<h4>{scopeWideSearchType === "Organization" ? "ORGANIZATIONAL" : "PORTFOLIO"}</h4>
						<span>Search Results</span>
					</Flex>
				</FlexCenter>
				<IconButtonV5
					IconComponent={CloseIcon}
					onClick={closeScopeWideSearch}
					style={{alignSelf: "flex-end"}}
				/>
			</HeaderStyled>
			<FlexCenter
				style={{
					alignItems: "flex-start",
					height: `${expandedSearchFeatureBlock ? "calc(100% - 85px)" : "inherit"}`,
				}}
				$gap={baseDistance.lg}
			>
				{!isFilteredListEmpty ? (
					<>
						<ModuleLinks>
							<ul>
								{sectionFeatures
									.filter((f) => (expandedSearchFeatureBlock ? f === expandedSearchFeatureBlock : true))
									.map((feature: XyiconFeature) => {
										const arrayWithItems = filterFilteredListByFeature(filteredList, feature);
										if (arrayWithItems.length > 0) {
											return (
												<li
													key={feature}
													onClick={() => scrollToFeature(feature)}
												>
													{expandedSearchFeatureBlock === feature && <ActiveSectionIndicatorStyled />}
													<h4>{getFeatureMenuLabel(feature, appState.app.navigation.menus)}</h4>
													<span>{arrayWithItems.length}</span>
												</li>
											);
										}
									})}
							</ul>
						</ModuleLinks>
						<ModuleGroups
							ref={_element}
							$isExpanded={!!expandedSearchFeatureBlock}
						>
							{moduleGroupContent()}
						</ModuleGroups>
					</>
				) : (
					<VerticalFlex
						style={{alignItems: "center", alignSelf: "center", margin: "0 auto"}}
						$gap={baseDistance.md}
					>
						<BigSearchIcon style={{width: 42, height: 42}} />
						<p style={{fontSize: 32}}>
							{isLoading ? "Loading..." : queryString ? "No matching results found" : `Start typing to search in ${scopeWideSearchType}`}
						</p>
					</VerticalFlex>
				)}
			</FlexCenter>
		</ScopeWideSearchStyled>
	);
};

const ActiveSectionIndicatorStyled = styled.div`
	position: absolute;
	bottom: 0;
	left: 0;
	right: 0;
	background-color: ${colorPalette.primary.c500Primary};
	border-radius: ${radius.sm};
	height: 100%;
	width: ${baseDistance.xs};
`;

const HeaderStyled = styled.div`
	${FlexCenterStyle};
	justify-content: space-between;

	h4 {
		text-transform: uppercase;
	}
`;

const ModuleGroups = styled.div<{$isExpanded: boolean}>`
	${VerticalFlexStyle};
	flex: 1;

	${(props) => {
		if (props.$isExpanded) {
			return css`
				gap: ${baseDistance.sm};
				height: 100%;
				${SearchFeatureBlockStyled} {
					height: 100%;
					flex: 1;
					.items {
						max-height: 100%;
						overflow-y: auto;
					}
				}
			`;
		} else {
			return css`
				gap: ${baseDistance.md};
				height: calc(100% - 90px);
				overflow-y: auto;
			`;
		}
	}}
`;

const ModuleLinks = styled.div`
	width: 168px;
	font-size: ${fontSize.md};
	text-transform: uppercase;
	font-weight: ${fontWeight.bold};
	cursor: pointer;

	ul {
		li {
			${FlexCenterStyle};
			justify-content: space-between;
			height: 32px;
			position: relative;
			padding-left: ${baseDistance.sm};

			&:hover {
				background-color: ${colorPalette.gray.c200Light};
				border-radius: ${radius.sm};
			}

			span {
				width: 36px;
				text-align: center;
			}
		}
	}
`;

const ScopeWideSearchStyled = styled.div<{$shrinkSize: number}>`
	${VerticalFlexStyle}
	gap: ${baseDistance.md};
	background: ${colorPalette.white};
	position: absolute;
	right: 0;
	bottom: 0;
	width: ${(props) => `calc(100vw - ${props.$shrinkSize}px)`};
	height: ${`calc(100vh - ${TOPBAR_HEIGHT})`};
	z-index: ${zIndex.scopeWideSearchOverlay};
	padding: ${baseDistance.md};
	color: ${colorPalette.gray.c950};

	&:hover::-webkit-scrollbar-thumb {
		border-width: 0;
		transition: ease-in-out all 0.2s;
	}
`;
